import { ComboBox } from 'src/components/credit/ui/comboBox/ComboBox';
import { handleKeyDownYearMonth } from 'src/utils/helpers';
import { IError } from '../BusinessInfo';
import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { useCreditForm } from 'src/customHooks/useCreditForm';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import {
    formatDateOfBirth,
    formatPhone,
    formatPostalCode_CA,
    formatPostalCode_US,
} from 'src/utils/formatInputs';
import { getStateOptions, getTitleOptions, validateMI } from '../../utils';
import { NUMBER_FIVE, NUMBER_ZERO } from 'src/utils/constants';
import React, { useState } from 'react';

interface IProps {
    handlerEdit: Function;
    object: {
        relationship: string;
        firstName: string;
        mi: string;
        lastName: string;
        telephone: string;
        birthDate: string;
        addressLine1: string;
        addressLine2: string;
        city: string;
        province: string;
        postalCode: string;
        years: string;
        months: string;
        license: string;
        expirationDate: string;
    };
    errors: {
        relationship: IError;
        firstName: IError;
        mi: IError;
        lastName: IError;
        telephone: IError;
        birthDate: IError;
        addressLine1: IError;
        addressLine2: IError;
        city: IError;
        province: IError;
        postalCode: IError;
        years: IError;
        months: IError;
        license: IError;
        expirationDate: IError;
    };
    disabled: boolean;
}

const DriverComponent = ({ handlerEdit, object, errors, disabled }: IProps) => {
    const { businessComercial, occupationIndividualInformation } =
        useLocalization();
    const { markCreditFormTouched } = useCreditForm();
    const dealerDataInfo = useSelector((state: IUser) => {
        return state.dealerDataInfo;
    });
    const [oldPhone, setOldPhone] = useState<string>('');
    const [oldBirth, setOldBirth] = useState<string>('');
    const propertyName = 'driver';
    const isCanada =
        dealerDataInfo?.dealer.country === 'Canada' ||
        dealerDataInfo?.dealer.country === 'CA';
    const stateOptions = getStateOptions(
        isCanada,
        occupationIndividualInformation,
    );
    const titleOptions = getTitleOptions(businessComercial);
    const postalcodeMaxLen = isCanada ? Number('7') : Number('5');
    const postalcodeVal = !isCanada
        ? object.postalCode.slice(NUMBER_ZERO, NUMBER_FIVE)
        : object.postalCode;

    const handlePostalChange = (e: any) => {
        let value = e.target.value.trim().toUpperCase();
        if (value.length > postalcodeVal.length) {
            if (!isCanada) {
                value = formatPostalCode_US(value);
            } else {
                value = formatPostalCode_CA(value);
            }
        }
        handlerEdit(
            {
                name: 'postalCode',
                value,
            },
            propertyName,
        );
    };
    const validatePhone = (e: any) => {
        let aux: string = e.target.value
            .split('(')
            .join()
            .split(')')
            .join()
            .split('-')
            .join();
        aux = aux.replaceAll(',', '').replaceAll(' ', '');
        if (/^[0-9]*$/.test(aux)) {
            let newValue = '';
            if (e.target.value.length > oldPhone.length) {
                newValue = formatPhone(e.target.value);
            } else {
                newValue = e.target.value;
            }
            handlerEdit(
                {
                    name: e.target.name,
                    value: newValue,
                },
                propertyName,
            );
            setOldPhone(e.target.value);
        }
        markCreditFormTouched();
    };
    const notDoubleZero = (value: string) => {
        if (
            value.length === Number('2') &&
            value.substring(Number('1')) === '0' &&
            value === '00'
        )
            return true;
        return false;
    };
    const validateBirth = (e: any) => {
        let aux: string = e.target.value
            .split('(')
            .join()
            .split(')')
            .join()
            .split('-')
            .join()
            .split('/')
            .join();
        aux = aux.replaceAll(',', '').replaceAll(' ', '');
        if (/^[0-9]*$/.test(aux)) {
            let newValue = '';
            if (e.target.value.length > oldBirth.length) {
                newValue = formatDateOfBirth(e.target.value);
            } else {
                newValue = e.target.value;
            }
            handlerEdit(
                {
                    name: e.target.name,
                    value: newValue,
                },
                propertyName,
            );
            setOldBirth(e.target.value);
        }
        markCreditFormTouched();
    };

    const validateYears = (e: any) => {
        const {
            target: { value },
        } = e;
        if (value.length < Number('3') && value < Number('100')) {
            if (value.includes('-') === false) {
                if (notDoubleZero(value)) return;
                handlerEdit(e.target, propertyName);
            }
        }
        markCreditFormTouched();
    };

    const validateMonths = (e: any) => {
        const {
            target: { value },
        } = e;
        if (value.length < Number('3') && value < Number('12')) {
            if (value.includes('-') === false) {
                if (notDoubleZero(value)) return;
                handlerEdit(e.target, propertyName);
            }
        }
        markCreditFormTouched();
    };
    return (
        <div className={'business-driver u-mt-large'}>
            <span className='c-form-section__header-content'>
                <span className='c-form-section__title  js-form-section__trigger'>
                    <span
                        className='c-form-section__title-text form-section__title-font'
                        role='heading'
                    >
                        {businessComercial.DRIVER_TITLE}
                    </span>
                </span>
            </span>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/1  u-1/1@xs'>
                        <span>{businessComercial.DRIVER_INFO}</span>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3 u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {businessComercial.DRIVER_RELATIONSHIP}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <ComboBox
                                        name={'optionBusinessTitle'}
                                        options={titleOptions}
                                        defaultValue={object.relationship}
                                        placeholder={
                                            businessComercial.OWNERS_CHOOSETITLE
                                        }
                                        selectedOption={object.relationship}
                                        onSelect={(value) => {
                                            handlerEdit(
                                                { name: 'relationship', value },
                                                propertyName,
                                            );
                                        }}
                                        disabled={disabled}
                                    />
                                    {errors.relationship.error &&
                                        errors.relationship.msg && (
                                            <span className={'c-error-message'}>
                                                {errors.relationship.msg}
                                            </span>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3 u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_NAME}
                            type={'text'}
                            name={'firstName'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.firstName}
                            maxlength={20}
                            asTouched={errors.firstName.error}
                            asErrors={errors.firstName.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/3 u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_MI}
                            type={'text'}
                            name={'mi'}
                            handlerChange={(e: any) => {
                                markCreditFormTouched();
                                const { value } = e.target;
                                if (validateMI(value) || value === '')
                                    handlerEdit(e.target, propertyName);
                            }}
                            value={object.mi}
                            maxlength={1}
                            asTouched={errors.mi.error}
                            asErrors={errors.mi.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_LASTNAME}
                            type={'text'}
                            name={'lastName'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.lastName}
                            maxlength={20}
                            asTouched={errors.lastName.error}
                            asErrors={errors.lastName.msg}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_TELEPHONE}
                            type={'text'}
                            name={'telephone'}
                            handlerChange={validatePhone}
                            value={object.telephone}
                            maxlength={14}
                            min={0}
                            asTouched={errors.telephone.error}
                            asErrors={errors.telephone.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_BIRTHDATE}
                            type={'text'}
                            name={'birthDate'}
                            handlerChange={validateBirth}
                            value={object.birthDate}
                            asTouched={errors.birthDate.error}
                            asErrors={errors.birthDate.msg}
                            maxlength={Number('10')}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2 u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_ADDRESS1}
                            type={'text'}
                            name={'addressLine1'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.addressLine1}
                            asTouched={errors.addressLine1.error}
                            asErrors={errors.addressLine1.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_ADDRESS2}
                            type={'text'}
                            name={'addressLine2'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.addressLine2}
                            asTouched={errors.addressLine2.error}
                            asErrors={errors.addressLine2.msg}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3 u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_CITY}
                            type={'text'}
                            name={'city'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.city}
                            asTouched={errors.city.error}
                            asErrors={errors.city.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <div className='c-form-field'>
                            <label
                                htmlFor='m1l7e063'
                                className='c-form-field__label'
                            >
                                {businessComercial.DRIVER_PROVINCE}
                            </label>
                            <div className='c-form-field__box'>
                                <div className='c-input'>
                                    <ComboBox
                                        name={'optionBusinessProvince'}
                                        options={stateOptions}
                                        defaultValue={object.province}
                                        placeholder={
                                            businessComercial.APPLICANT_CHOOSEPROVINCE
                                        }
                                        selectedOption={object.province}
                                        onSelect={(value) => {
                                            handlerEdit(
                                                { name: 'province', value },
                                                propertyName,
                                            );
                                        }}
                                        disabled={disabled}
                                    />
                                    {errors.province.error &&
                                        errors.province.msg && (
                                            <span className={'c-error-message'}>
                                                {errors.province.msg}
                                            </span>
                                        )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_POSTALCODE}
                            type={'text'}
                            name={'postalCode'}
                            handlerChange={handlePostalChange}
                            maxlength={postalcodeMaxLen}
                            value={postalcodeVal}
                            asTouched={errors.postalCode.error}
                            asErrors={errors.postalCode.msg}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_YEARS}
                            type={'text'}
                            name={'years'}
                            handlerChange={validateYears}
                            value={object.years}
                            maxlength={2}
                            min={0}
                            asTouched={errors.years.error}
                            asErrors={errors.years.msg}
                            handleKeyPress={handleKeyDownYearMonth}
                        />
                    </div>
                    <div className='o-layout__item  u-1/3  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_MONTHS}
                            type={'text'}
                            name={'months'}
                            handlerChange={validateMonths}
                            value={object.months}
                            maxlength={2}
                            min={0}
                            max={11}
                            asTouched={errors.months.error}
                            asErrors={errors.months.msg}
                            handleKeyPress={handleKeyDownYearMonth}
                        />
                    </div>
                </div>
            </div>
            <div className='o-fieldset__row'>
                <div className='o-layout'>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_LICENSE}
                            type={'text'}
                            name={'license'}
                            handlerChange={(e: any) => {
                                handlerEdit(e.target, propertyName);
                            }}
                            value={object.license}
                            min={0}
                            asTouched={errors.license.error}
                            asErrors={errors.license.msg}
                        />
                    </div>
                    <div className='o-layout__item  u-1/2  u-1/1@xs'>
                        <Input
                            labelName={businessComercial.DRIVER_DATE}
                            type={'text'}
                            name={'expirationDate'}
                            handlerChange={validateBirth}
                            value={object.expirationDate}
                            asTouched={errors.expirationDate.error}
                            asErrors={errors.expirationDate.msg}
                            maxlength={Number('10')}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DriverComponent;
